import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface AppApiKeyPermissionRef {
  _id: string;
  name?: string;
  entity?: string;
  abilities?: string[];
}

export interface AppApiKeyRow {
  id: number;
  name: string;
  isActive: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
  permissions_ref?: AppApiKeyPermissionRef[];
}

export interface AppApiKeyCreatePayload {
  name: string;
  expiresAt?: string;
  isActive?: boolean;
  /** MongoDB Permission document ids (same as Role permissions). */
  permissions_ref?: string[];
}

export type AppApiKeyUpdatePayload = Partial<Omit<AppApiKeyCreatePayload, 'expiresAt'>> & {
  expiresAt?: string | null;
};

export interface AppApiKeyCreated extends AppApiKeyRow {
  apiKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppApiKeysService {
  private readonly base = '/api/app-api-keys';

  constructor(private http: HttpClient) {}

  list(): Promise<AppApiKeyRow[]> {
    return firstValueFrom(this.http.get<AppApiKeyRow[]>(this.base));
  }

  create(body: AppApiKeyCreatePayload): Promise<AppApiKeyCreated> {
    return firstValueFrom(this.http.post<AppApiKeyCreated>(`${this.base}/add`, body));
  }

  update(id: number, body: AppApiKeyUpdatePayload): Promise<AppApiKeyRow> {
    return firstValueFrom(
      this.http.patch<AppApiKeyRow>(`${this.base}/update/${id}`, body),
    );
  }

  delete(id: number): Promise<{ ok: boolean }> {
    return firstValueFrom(this.http.delete<{ ok: boolean }>(`${this.base}/${id}`));
  }

  rotate(id: number): Promise<AppApiKeyCreated> {
    return firstValueFrom(
      this.http.post<AppApiKeyCreated>(`${this.base}/rotate/${id}`, {}),
    );
  }
}
